import { translationBaseKey } from '../../../decorators/translation-base-key.decorator';
import { CalculationRunOptions } from '../calculation-run.options';
import {
  advancedFormGroup,
  advancedFormControl,
} from '@magieno/common';
import { CalculationKeynameEnum } from '../../../enums/calculations/calculation-keyname.enum';
import { FieldTypeEnum } from '../../../enums/field-type.enum';
import { FormControlTypeEnum } from '@magieno/common';
import {
  IsBoolean,
  IsInt,
  IsNotEmpty,
  IsOptional,
  Max,
  Min,
  validateIf,
  ValidateIf,
  ValidateNested,
} from '@pristine-ts/class-validator';
import { ErrorMessageBuilder } from '../../../builders/error-message.builder';
import { CalculationDate } from '../../../models/calculation.date';


@translationBaseKey('salary-contributions-options')
export class SalaryContributionsOptions extends CalculationRunOptions {
  @advancedFormControl({
    type: FormControlTypeEnum.Hidden,
  })
  calculationKeyname: CalculationKeynameEnum = CalculationKeynameEnum.SalaryContributions;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Salaire brut',
        en: 'Gross salary',
      },
      tooltip: {
        fr: 'Ce champ représente le montant total du salaire brut de l\'employé, y compris le salaire gagné à l\'extérieur du Canada. Le salaire brut comprend la rémunération totale avant toute déduction. Il est utilisé pour calculer les cotisations au régime de retraite et autres avantages liés à l\'emploi.',
        en: 'This field represents the total amount of the employee\'s gross salary, including the salary earned outside Canada. Gross salary includes the total compensation before any deductions. It is used to calculate contributions to the retirement plan and other employment-related benefits.',
      },
      description: {
        fr: `<h6>Salaire Brut</h6>
<p>Le Salaire Brut consiste au salaire avant retenues à la source.</p>
`,
      },
    },
    fieldType: FieldTypeEnum.Money,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  employmentIncome: number;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Masse salariale totale (incluant les indemnités pour accident du travail non remboursées)',
        en: 'Total payroll (including unreimbursed workers\' compensation benefits)',
      },
      tooltip: {
        fr: 'Ce champ représente la masse salariale totale de l\'employeur, y compris les indemnités pour accident du travail non remboursées. La masse salariale totale est utilisée pour calculer les cotisations aux régimes sociaux et les impôts sur le revenu des employés.',
        en: 'This field represents the total payroll of the employer, including unreimbursed workers\' compensation benefits. The total payroll is used to calculate contributions to social programs and income taxes for employees.',
      },
    },
    fieldType: FieldTypeEnum.Money,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @validateIf((object: SalaryContributionsOptions, root, currentPath): boolean => {
    return object.shouldFSSBeCalculated === true;
  })
  totalPayroll?: number;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Masse salariale totale se rapportant à des activités des secteurs primaire et manufacturier',
        en: 'Total payroll related to primary and manufacturing sectors activities',
      },
      tooltip: {
        fr: 'Ce champ représente la masse salariale totale se rapportant aux activités des secteurs primaire et manufacturier de l\'employeur. Elle est utilisée pour des évaluations spécifiques ou des avantages fiscaux liés à ces secteurs d\'activité.',
        en: 'This field represents the total payroll related to the primary and manufacturing sectors activities of the employer. It is used for specific assessments or tax benefits related to these business sectors.',
      },
    },
    fieldType: FieldTypeEnum.Money,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @validateIf((object: SalaryContributionsOptions, root, currentPath): boolean => {
    return object.shouldFSSBeCalculated === true;
  })
  totalPayrollForPrimarySector?: number;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'L\'employeur est-il du secteur public?',
        en: 'Is the employer from the public sector?',
      },
      tooltip: {
        fr: 'Ce champ indique si l\'employeur est du secteur public. Le secteur public comprend les organismes gouvernementaux, les sociétés d\'État et les administrations publiques. Cette information est pertinente pour certaines déductions ou crédits d\'impôt applicables aux employeurs du secteur public.',
        en: 'This field indicates whether the employer is from the public sector. The public sector includes government agencies, state-owned enterprises, and public administrations. This information is relevant for certain deductions or tax credits applicable to employers in the public sector.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @validateIf((object: SalaryContributionsOptions, root, currentPath): boolean => {
    return object.shouldFSSBeCalculated === true;
  })
  isEmployerFromPublicSector?: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Date de naissance',
        en: 'Birthdate',
      },
      tooltip: {
        fr: 'Ce champ représente la date de naissance du contribuable. La date de naissance est utilisée pour déterminer l\'âge du contribuable, ce qui peut avoir des implications fiscales pour certaines prestations ou déductions liées à l\'âge.',
        en: 'This field represents the taxpayer\'s birthdate. The birthdate is used to determine the taxpayer\'s age, which may have tax implications for certain benefits or deductions related to age.',
      },
    },
    fieldType: FieldTypeEnum.CalculationDate,
    type: FormControlTypeEnum.DatePicker,
  })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateNested()
  @IsOptional()
  birthdate?: CalculationDate;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Le contribuable est-il décédé dans l\'année?',
        en: 'Is the taxpayer deceased in the year?',
      },
      tooltip: {
        fr: 'Ce champ indique si le contribuable est décédé au cours de l\'année fiscale. Le décès du contribuable peut entraîner des conséquences fiscales spécifiques, telles que des règles particulières de succession.',
        en: 'This field indicates whether the taxpayer has deceased during the fiscal year. The taxpayer\'s death may have specific tax consequences, such as special succession rules.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  isTaxpayerDead: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Date du décès',
        en: 'Death date',
      },
      tooltip: {
        fr: 'Ce champ représente la date de décès du contribuable, le cas échéant. La date de décès est utilisée pour des fins fiscales spécifiques, telles que l\'établissement des biens du défunt et la succession.',
        en: 'This field represents the taxpayer\'s death date, if applicable. The death date is used for specific tax purposes, such as establishing the deceased\'s estate and succession.',
      },
    },
    fieldType: FieldTypeEnum.CalculationDate,
    type: FormControlTypeEnum.DatePicker,
  })
  @ValidateNested()
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf((object: SalaryContributionsOptions) => object.isTaxpayerDead === true)
  deathdate?: CalculationDate; // todo this should be nullable

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Taux de versement périodique',
        en: 'Recurring payment rate',
      },
      tooltip: {
        fr: 'Versement que certains employeurs au Québec doivent payer au courant de l\'année . Il s\'agit essentiellement d\'un acompte sur votre cotisation annuelle.',
        en: 'This field represents the recurring payment rate applicable to certain types of income, such as annuities or installment payments. The recurring payment rate is used to calculate the amount of recurring payments.',
      },
      description: {
        fr: '# Le Taux périodique de la CNESST : Explication détaillée\n' +
          '\n' +
          'Le **Taux périodique** de la Commission des normes, de l\'équité, de la santé et de la sécurité du travail (CNESST) est un versement que certains employeurs au Québec doivent payer au courant de l\'année pour financer le régime de santé et de sécurité du travail. Il s\'agit essentiellement d\'un acompte sur votre cotisation annuelle.\n' +
          '\n' +
          'Ce système de versements périodiques vise à répartir la charge financière de la cotisation annuelle sur plusieurs paiements, plutôt qu\'en un seul montant à la fin de l\'année.\n' +
          '\n' +
          '---\n' +
          '\n' +
          '### Qui doit payer le Taux périodique ?\n' +
          '\n' +
          'Ce ne sont pas tous les employeurs qui sont tenus de faire des versements périodiques. Vous êtes visé si votre **cotisation de l\'année précédente était de 400 $ ou plus**.\n' +
          '\n' +
          '* **Si votre cotisation de l\'année précédente était inférieure à 400 $**, vous n\'avez pas de versements périodiques à faire. Vous paierez la totalité de votre cotisation lors de la réception de votre « Décision de classification et de cotisation » en début d\'année.\n' +
          '* **Si votre cotisation de l\'année précédente était de 400 $ ou plus**, vous devrez effectuer des paiements tout au long de l\'année.\n' +
          '\n' +
          '---\n' +
          '\n' +
          '### Comment fonctionne le Taux périodique ?\n' +
          '\n' +
          'La CNESST estime le montant de votre cotisation pour l\'année en cours en se basant sur la masse salariale que vous avez déclarée l\'année précédente et le taux de prime de votre unité de classification. Le Taux périodique est un pourcentage de cette estimation.\n' +
          '\n' +
          'Le paiement est généralement effectué de manière trimestrielle, mais la fréquence peut varier. La CNESST vous enverra un document intitulé « Versement périodique » qui indique le montant à payer et la date d\'échéance.\n' +
          '\n' +
          '**Le calcul est simple :**\n' +
          '\n' +
          '1.  **Estimation de la masse salariale :** La CNESST utilise la masse salariale que vous avez déclarée pour l\'année précédente.\n' +
          '2.  **Calcul de la cotisation annuelle estimée :** Elle multiplie cette masse salariale par le taux de prime de votre secteur d\'activité.\n' +
          '3.  **Calcul du versement périodique :** Elle applique un pourcentage à cette cotisation estimée pour déterminer le montant de chaque versement.\n' +
          '\n' +
          'Il est crucial de se rappeler que ce montant est une **estimation**. Le solde final sera ajusté à la fin de l\'année lorsque vous produirez votre « Déclaration des salaires » avec votre masse salariale réelle pour l\'année écoulée.\n' +
          '\n' +
          '---\n' +
          '\n' +
          '### Exemples concrets\n' +
          '\n' +
          'Voici deux scénarios pour illustrer le concept.\n' +
          '\n' +
          '#### Exemple 1 : Petite entreprise non assujettie au Taux périodique\n' +
          '\n' +
          '* **Entreprise :** « Nettoyage Pro-Fenêtres Inc. »\n' +
          '* **Cotisation CNESST pour 2024 :** 350 $\n' +
          '\n' +
          'Comme la cotisation de 2024 était inférieure à 400 $, l\'entreprise n\'aura **aucun versement périodique** à faire au cours de l\'année 2025. Elle paiera la totalité de sa cotisation pour 2025 en un seul versement, après avoir reçu sa décision de classification au début de 2026.\n' +
          '\n' +
          '#### Exemple 2 : PME assujettie au Taux périodique\n' +
          '\n' +
          '* **Entreprise :** « Construction Tremblay & Fils »\n' +
          '* **Masse salariale déclarée pour 2024 :** 500 000 $* **Taux de prime pour son secteur en 2025 :** 3,50$ par 100 $ de salaire (soit 3,5 %)\n' +
          '\n' +
          '**1. Estimation de la cotisation pour 2025 :**\n' +
          'La CNESST estime que la masse salariale de 2025 sera similaire à celle de 2024.\n' +
          '```\n' +
          'Calcul : (500 000 $ / 100) * 3,50 $ = 17 500 $\n' +
          '```\n' +
          '\n' +
          '**2. Calcul du versement périodique :**\n' +
          'La CNESST demande à l\'entreprise de payer un acompte sur ce montant estimé, réparti sur l\'année. Supposons qu\'elle envoie quatre demandes de versement périodique. Chaque versement pourrait être d\'environ :\n' +
          '```\n' +
          'Calcul : 17 500 $ / 4 = 4 375 $\n' +
          '```\n' +
          'L\'entreprise recevra donc un avis de paiement pour un montant de 4 375 $ à chaque trimestre.\n' +
          '\n' +
          '**3. Ajustement en fin d\'année :**\n' +
          'Au début de 2026, l\'entreprise produit sa « Déclaration des salaires » pour l\'année 2025. Elle déclare une masse salariale réelle de 520 000 $ (plus élevée que l\'estimation).\n' +
          '\n' +
          '* **Cotisation réelle pour 2025 :**\n' +
          '    ```\n' +
          '    (520 000 $ / 100) * 3,50 $ = 18 200 $\n' +
          '    ```\n' +
          '* **Total des versements périodiques déjà payés :**\n' +
          '    ```\n' +
          '    4 375 $ * 4 = 17 500 $\n' +
          '    ```\n' +
          '* **Solde à payer :**\n' +
          '    ```\n' +
          '    18 200 $ - 17 500 $ = 700 $\n' +
          '    ```\n' +
          '\n' +
          'L\'entreprise devra donc payer ce solde de 700 $ à la CNESST pour régler sa cotisation de 2025. Si sa masse salariale réelle avait été inférieure à l\'estimation, elle aurait pu recevoir un crédit ou un remboursement.\n',
        en: '',
      },
    },
    fieldType: FieldTypeEnum.Number,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @Min(0, { buildErrorMessage: ErrorMessageBuilder.build })
  @Max(100, { buildErrorMessage: ErrorMessageBuilder.build })
  @validateIf((object: SalaryContributionsOptions, root, currentPath): boolean => {
    return object.shouldCNESSTBeCalculated === true;
  })
  recurringPaymentRate?: number;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Calculer la cotisation RRQ de l\'employé et de l\'employeur?',
        en: 'Calculate employee and employer\'s QPP contribution?',
      },
      tooltip: {
        fr: 'Ce champ indique si la cotisation au Régime de rentes du Québec (RRQ) doit être calculée pour l\'employé et l\'employeur. Le RRQ est un régime de pension géré par le gouvernement du Québec, auquel les travailleurs et les employeurs contribuent.',
        en: 'This field indicates whether the contribution to the Quebec Pension Plan (QPP) should be calculated for the employee and the employer. The QPP is a pension plan administered by the Quebec government, to which workers and employers contribute.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean()
  @IsNotEmpty()
  shouldQPPBeCalculated: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Calculer la cotisation RQAP de l\'employé et de l\'employeur?',
        en: 'Calculate employee and employer\'s QPIP contribution?',
      },
      tooltip: {
        fr: 'Ce champ indique si la cotisation au Régime québécois d\'assurance parentale (RQAP) doit être calculée pour l\'employé et l\'employeur. Le RQAP est un régime d\'assurance parentale géré par le gouvernement du Québec, auquel les travailleurs et les employeurs contribuent.',
        en: 'This field indicates whether the contribution to the Quebec Parental Insurance Plan (QPIP) should be calculated for the employee and the employer. The QPIP is a parental insurance plan administered by the Quebec government, to which workers and employers contribute.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  shouldQPIPBeCalculated: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Calculer la cotisation à l\'assurance-emploi de l\'employé et de l\'employeur?',
        en: 'Calculate employee and employer\'s EI contribution?',
      },
      tooltip: {
        fr: 'Ce champ indique si la cotisation à l\'assurance-emploi (AE) doit être calculée pour l\'employé et l\'employeur. L\'AE est un programme d\'assurance-chômage au Canada qui fournit des prestations aux travailleurs qui perdent leur emploi.',
        en: 'This field indicates whether the contribution to Employment Insurance (EI) should be calculated for the employee and the employer. EI is a Canadian unemployment insurance program that provides benefits to workers who lose their jobs.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  shouldEIBeCalculated: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Calculer la cotisation FSS de l\'employé et de l\'employeur?',
        en: 'Calculate employee and employer\'s FSS contribution?',
      },
      tooltip: {
        fr: 'Ce champ indique si la cotisation au Fonds des services de santé (FSS) doit être calculée pour l\'employé et l\'employeur. Le FSS est un fonds qui finance les services de santé au Québec.',
        en: 'This field indicates whether the contribution to the Health Services Fund (FSS) should be calculated for the employee and the employer. The FSS is a fund that finances health services in Quebec.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  shouldFSSBeCalculated: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Calculer la cotisation CNT de l\'employé et de l\'employeur?',
        en: 'Calculate employee and employer\'s CNT contribution?',
      },
      tooltip: {
        fr: 'Ce champ indique si la cotisation au Comité paritaire de l\'industrie de la construction (CNT) doit être calculée pour l\'employé et l\'employeur. Le CNT est un organisme paritaire qui gère les contributions pour l\'industrie de la construction au Québec.',
        en: 'This field indicates whether the contribution to the Joint Labor-Management Construction Committee (CNT) should be calculated for the employee and the employer. The CNT is a joint organization that manages contributions for the construction industry in Quebec.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  shouldCNTBeCalculated: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Calculer la cotisation CNESST de l\'employé et de l\'employeur?',
        en: 'Calculate employee and employer\'s CNESST contribution?',
      },
      tooltip: {
        fr: 'Ce champ indique si la cotisation à la Commission des normes, de l\'équité, de la santé et de la sécurité du travail (CNESST) doit être calculée pour l\'employé et l\'employeur. La CNESST gère les régimes d\'indemnisation des travailleurs au Québec.',
        en: 'This field indicates whether the contribution to the Commission des normes, de l\'équité, de la santé et de la sécurité du travail (CNESST) should be calculated for the employee and the employer. The CNESST manages workers\' compensation schemes in Quebec.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  shouldCNESSTBeCalculated: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Le salarié est-il assujetti à l\'assurance-emploi?',
        en: 'Is the employee subject to employment insurance?',
      },
      tooltip: {
        fr: 'Ce champ indique si le salarié est assujetti à l\'assurance-emploi (AE). L\'AE est un programme d\'assurance-chômage au Canada qui fournit des prestations aux travailleurs qui perdent leur emploi.',
        en: 'This field indicates whether the employee is subject to Employment Insurance (EI). EI is a Canadian unemployment insurance program that provides benefits to workers who lose their jobs.',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  isEmployeeSubjectToEI: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Montant de salaire pour l\'assurance-emploi',
        en: 'Salary amount for the employment insurance',
      },
      tooltip: {
        fr: 'Ce champ indique le montant de salaire pour le calcul de l\'assurance-emploi.',
        en: 'This field indicates the salary amount used for the employment insurance contribution.',
      },
    },
    fieldType: FieldTypeEnum.Money,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @validateIf((object: SalaryContributionsOptions, root, currentPath): boolean => {
    return !object.isEmployeeSubjectToEI;
  })
  @IsOptional()
  employmentIncomeForEI?: number;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Année',
        en: 'Year',
      },
      tooltip: {
        fr: 'Ce champ indique l\'année pour laquelle les calculs sont effectués.',
        en: 'This field indicates the year for which the calculations are being performed.',
      },
    },
    fieldType: FieldTypeEnum.Number,
    type: FormControlTypeEnum.Select,
    items: [
      {
        label: {
          fr: '2021',
        },
        id: 2021,
      },
      {
        label: {
          fr: '2022',
        },
        id: 2022,
      },
      {
        label: {
          fr: '2023',
        },
        id: 2023,
      },
      {
        label: {
          fr: '2024',
        },
        id: 2024,
      },
      {
        label: {
          fr: '2025',
        },
        id: 2025,
      },
    ],
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @Min(2021, { buildErrorMessage: ErrorMessageBuilder.build })
  @Max(2025, { buildErrorMessage: ErrorMessageBuilder.build })
  fiscalYear: number;
}
